import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';

import type { TimetableMatrix } from '../../utils/fixedRoutes/buildTimetableMatrix';
import TimetableGrid, { TimetableGridMode } from './TimetableGrid';
import RouteMiniMap, { MiniMapStop } from './RouteMiniMap';

/**
 * Composes the bustimes-style timetable grid with a companion "route map" window. Holds the
 * shared focused-stop state: hovering a stop row in the grid highlights it on the map, and
 * clicking a stop on the map highlights its grid row. Reused by both the hosted-network
 * viewer and the own-route Timetable tab.
 */

interface TimetableGridViewProps {
  matrix: TimetableMatrix;
  routeColor?: string;
  /** Corridor heading, e.g. "Carlisle – Cockermouth – West Cumbria Hospital". */
  heading?: string;
  /** Optional summary / controls rendered under the heading (trip count, headway, etc.). */
  summary?: React.ReactNode;
  miniMap?: { coordinates: Array<[number, number]>; stops: MiniMapStop[] };
  defaultMode?: TimetableGridMode;
  onModeChange?: (mode: TimetableGridMode) => void;
}

function TimetableGridView({
  matrix,
  routeColor,
  heading,
  summary,
  miniMap,
  defaultMode,
  onModeChange,
}: TimetableGridViewProps): React.ReactElement {
  const { t } = useTranslation(['mapCore']);
  const [focusedStopId, setFocusedStopId] = useState<string | null>(null);

  const hasMap = !!miniMap && (miniMap.coordinates.length >= 2 || miniMap.stops.length > 0);

  return (
    <Box>
      {heading && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: summary ? 0.25 : 0.75 }}>
          {heading}
        </Typography>
      )}
      {summary && (
        <Box sx={{ mb: 0.75 }}>{summary}</Box>
      )}

      {/* Map on top (drawer-narrow) / beside the grid (wide). The grid scrolls inside its
          own box, so the route map stays in view while the rider scans the timetable. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, alignItems: 'stretch' }}>
        {hasMap && (
          <Paper
            variant="outlined"
            sx={{
              width: { xs: '100%', md: 240 },
              flexShrink: 0,
              overflow: 'hidden',
              alignSelf: { xs: 'stretch', md: 'flex-start' },
            }}
            onMouseLeave={() => setFocusedStopId(null)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                color: 'text.secondary',
              }}
            >
              <MapIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {t('timetableGrid.routeMap')}
              </Typography>
            </Box>
            <RouteMiniMap
              coordinates={miniMap!.coordinates}
              stops={miniMap!.stops}
              routeColor={routeColor}
              focusedStopId={focusedStopId}
              onFocusStop={setFocusedStopId}
              height={200}
            />
          </Paper>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }} onMouseLeave={() => setFocusedStopId(null)}>
          <TimetableGrid
            matrix={matrix}
            routeColor={routeColor}
            focusedStopId={focusedStopId}
            onFocusStop={setFocusedStopId}
            defaultMode={defaultMode}
            onModeChange={onModeChange}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default TimetableGridView;
